// Copyright 2025, University of Maryland, All Rights Reserved
import { apiClient } from './api-client'
import type { BatchRunResponse, MigrationInfo, RunMigrationResponse } from './types/migrations'

/**
 * List all migrations with their current status.
 * Requires global admin privileges.
 */
export async function listMigrations(): Promise<MigrationInfo[]> {
  const response = await apiClient.get<MigrationInfo[]>('/admin/migrations')
  return response.data
}

/**
 * Queue a migration to run on the jobs lambda.
 * Returns job_id for status tracking.
 * If migration is already running, returns existing job_id.
 */
export async function runMigration(migrationId: string): Promise<RunMigrationResponse> {
  const response = await apiClient.post<RunMigrationResponse>(
    `/admin/migrations/${encodeURIComponent(migrationId)}/run`
  )
  return response.data
}

/**
 * Queue multiple migrations to run sequentially on the jobs lambda.
 */
export async function runMigrationBatch(migrationIds: string[]): Promise<BatchRunResponse> {
  const response = await apiClient.post<BatchRunResponse>(
    '/admin/migrations/run-batch',
    { migrationIds }
  )
  return response.data
}

/**
 * Queue a rollback of a migration's backed-up (non-resettable) changes on the jobs lambda.
 * Replays the migration's BACKUP# before-images and resets it to pending. Returns job_id.
 */
export async function rollbackMigration(migrationId: string): Promise<RunMigrationResponse> {
  const response = await apiClient.post<RunMigrationResponse>(
    `/admin/migrations/${encodeURIComponent(migrationId)}/rollback`
  )
  return response.data
}

/**
 * Fetch migration execution log from S3.
 * Returns plain text log content.
 */
export async function getMigrationLog(migrationId: string): Promise<string> {
  const response = await apiClient.get<string>(
    `/admin/migrations/${encodeURIComponent(migrationId)}/log`,
    { responseType: 'text' }
  )
  return response.data
}
